import { Action } from '@/app/actions/actions';
import type { ActionSpecRecord } from '@/app/actions/actionsSpec';
import { getShowAIAnalyst, setShowAIAnalyst, toggleShowAIAnalyst } from '@/app/ai/atoms/aiAnalystAtoms';
import { events } from '@/app/events/events';
import { openCodeEditor } from '@/app/grid/actions/openCodeEditor';
import { sheets } from '@/app/grid/controller/Sheets';
import { zoomIn, zoomInOut, zoomOut, zoomReset, zoomToFit, zoomToSelection } from '@/app/gridGL/helpers/zoom';
import { pageUpDown } from '@/app/gridGL/interaction/viewportHelper';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { embedDisableZoom, isAiDisabled, isReadonly } from '@/app/helpers/isEmbed';
import { KeyboardSymbols } from '@/app/helpers/keyboardSymbols';
import { resolveGridReferenceMention } from '@/app/ui/components/promptEditor/referenceToMention';
import { AIIcon, CodeIcon, GoToIcon, HistoryIcon, MentionIcon } from '@/shared/components/Icons';
import { trackEvent } from '@/shared/utils/analyticsEvents';
import { createTextContent } from 'quadratic-shared/ai/helpers/message.helper';

type ViewActionSpec = Pick<
  ActionSpecRecord,
  | Action.CmdClick
  | Action.ZoomIn
  | Action.ZoomOut
  | Action.ZoomToSelection
  | Action.ZoomToFit
  | Action.ZoomTo50
  | Action.ZoomTo100
  | Action.ZoomTo200
  | Action.ZoomReset
  | Action.GridPanMode
  | Action.ShowCommandPalette
  | Action.TogglePresentationMode
  | Action.CloseOverlay
  | Action.SwitchSheetNext
  | Action.SwitchSheetPrevious
  | Action.PageUp
  | Action.PageDown
  | Action.ShowGoToMenu
  | Action.ShowCellEditHistory
  | Action.ShowCellTypeMenu
  | Action.ToggleAIAnalyst
  | Action.AddReferenceToAIAnalyst
  | Action.TraceValue
>;

export type ViewActionArgs = {
  [Action.AddReferenceToAIAnalyst]: string;
  [Action.TraceValue]: string;
};

export const viewActionsSpec: ViewActionSpec = {
  [Action.CmdClick]: {
    label: () => `${KeyboardSymbols.Command}+click`,
    run: () => {},
  },
  [Action.ZoomIn]: {
    label: () => 'Zoom in',
    isAvailable: () => !embedDisableZoom,
    run: () => {
      zoomIn();
    },
  },
  [Action.ZoomOut]: {
    label: () => 'Zoom out',
    isAvailable: () => !embedDisableZoom,
    run: () => {
      zoomOut();
    },
  },
  [Action.ZoomToSelection]: {
    label: () => 'Zoom to selection',
    isAvailable: () => !embedDisableZoom,
    run: () => {
      zoomToSelection();
    },
  },
  [Action.ZoomToFit]: {
    label: () => 'Zoom to fit',
    isAvailable: () => !embedDisableZoom,
    run: () => {
      zoomToFit();
    },
  },
  [Action.ZoomTo50]: {
    label: () => 'Zoom to 50%',
    isAvailable: () => !embedDisableZoom,
    run: () => {
      zoomInOut(0.5);
    },
  },
  [Action.ZoomTo100]: {
    label: () => 'Zoom to 100%',
    isAvailable: () => !embedDisableZoom,
    run: () => {
      zoomInOut(1);
    },
  },
  [Action.ZoomTo200]: {
    label: () => 'Zoom to 200%',
    isAvailable: () => !embedDisableZoom,
    run: () => {
      zoomInOut(2);
    },
  },
  [Action.ZoomReset]: {
    label: () => 'Move to origin',
    run: () => zoomReset(),
  },
  [Action.GridPanMode]: {
    label: () => 'Grid pan mode',
    run: () => {}, // TODO(ayush): add this when refactoring shortcuts to use action specs
  },
  [Action.ShowCommandPalette]: {
    label: () => 'Show command palette',
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showFeedbackMenu: false,
        showCellTypeMenu: false,
        showGoToMenu: false,
        showCellEditHistory: false,
        showShareFileMenu: false,
        showCommandPalette: !prev.showCommandPalette,
      }));
    },
  },
  [Action.TogglePresentationMode]: {
    label: () => 'Toggle presentation mode',
    run: () => {
      const prev = gridSettings.getGridSettings();
      gridSettings.setGridSettings({
        ...prev,
        presentationMode: !prev.presentationMode,
      });
    },
  },
  [Action.CloseOverlay]: {
    label: () => 'Close overlay',
    run: () => {
      const prevGridSettings = gridSettings.getGridSettings();
      gridSettings.setGridSettings({
        ...prevGridSettings,
        presentationMode: false,
      });
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showCellTypeMenu: false,
        showCommandPalette: false,
        showConnectionsMenu: undefined,
        showGoToMenu: false,
        showCellEditHistory: false,
        showFeedbackMenu: false,
        showRenameFileMenu: false,
        showShareFileMenu: false,
        showValidation: false,
      }));
    },
  },
  [Action.SwitchSheetNext]: {
    label: () => 'Switch sheet next',
    run: () => {
      if (sheets.size > 1) {
        const nextSheet = sheets.getNext(sheets.sheet.order) ?? sheets.getFirst();
        sheets.current = nextSheet.id;
      }
    },
  },
  [Action.SwitchSheetPrevious]: {
    label: () => 'Switch sheet previous',
    run: () => {
      if (sheets.size > 1) {
        const previousSheet = sheets.getPrevious(sheets.sheet.order) ?? sheets.getLast();
        sheets.current = previousSheet.id;
      }
    },
  },
  [Action.PageUp]: {
    label: () => 'Page up',
    run: () => pageUpDown(true),
  },
  [Action.PageDown]: {
    label: () => 'Page down',
    run: () => pageUpDown(false),
  },
  [Action.ShowGoToMenu]: {
    label: () => 'Go to',
    Icon: GoToIcon,
    isAvailable: () => !isReadonly,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showGoToMenu: true,
        showCellEditHistory: false,
      }));
    },
  },
  [Action.ShowCellEditHistory]: {
    label: () => 'Cell edit history',
    Icon: HistoryIcon,
    isAvailable: () => !isReadonly,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showCellEditHistory: !prev.showCellEditHistory,
        showGoToMenu: false,
      }));
    },
  },
  [Action.ShowCellTypeMenu]: {
    label: () => 'Code editor',
    Icon: CodeIcon,
    run: () => openCodeEditor(),
  },
  [Action.ToggleAIAnalyst]: {
    label: () => 'Chat',
    isAvailable: () => !isAiDisabled(),
    run: () => {
      toggleShowAIAnalyst();
    },
  },
  [Action.AddReferenceToAIAnalyst]: {
    label: () => 'Mention in chat',
    Icon: MentionIcon,
    isAvailable: () => !isAiDisabled(),
    run: (reference: ViewActionArgs[Action.AddReferenceToAIAnalyst]) => {
      trackEvent('[AIMentions].addReferenceFromGrid', {
        showAIAnalyst: getShowAIAnalyst(),
      });

      // Note: if we emit `aiAnalystInsertMention` immediately, the event
      // listener will not be set up yet inside the Analyst because its not
      // rendered yet. So if it's closed, we have to wait for it to be ready.
      const emitReferenceEvent = () => {
        events.emit('aiAnalystInsertMention', resolveGridReferenceMention(reference ?? ''));
      };
      if (getShowAIAnalyst()) {
        // AIAnalyst is already shown, emit immediately
        emitReferenceEvent();
      } else {
        // AIAnalyst needs to be shown first, wait for it to be ready
        setShowAIAnalyst(true);
        const handleReady = () => {
          events.off('aiAnalystReady', handleReady);
          emitReferenceEvent();
        };
        events.on('aiAnalystReady', handleReady);
      }

      gridSettings.setContextMenu({});
    },
  },
  [Action.TraceValue]: {
    label: () => 'Trace value',
    Icon: AIIcon,
    isAvailable: () => !isAiDisabled(),
    run: (reference: ViewActionArgs[Action.TraceValue]) => {
      const ref = reference ?? '';
      if (!ref) {
        gridSettings.setContextMenu({});
        return;
      }

      trackEvent('[GridContextMenu].traceValue', {
        showAIAnalyst: getShowAIAnalyst(),
      });

      const prompt = `Trace the value in @${ref}`;

      const submit = () => {
        events.emit('aiAnalystSubmitPrompt', {
          content: [createTextContent(prompt)],
          messageSource: 'TraceValue',
          context: {},
          messageIndex: 0,
          importFiles: [],
        });
      };

      if (getShowAIAnalyst()) {
        submit();
      } else {
        setShowAIAnalyst(true);
        const handleReady = () => {
          events.off('aiAnalystReady', handleReady);
          submit();
        };
        events.on('aiAnalystReady', handleReady);
      }

      gridSettings.setContextMenu({});
    },
  },
};
